import React, { useEffect, useMemo, useRef, useState } from "react";
import { FileMetadata } from "@domain/schemas/BI";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";

type ArtifactDownloadDialogProperties = {
  title: string;
  files: FileMetadata[];
  isLoading: boolean;
  errorMessage?: string | null;
  submitErrorMessage?: string | null;
  onClose: () => void;
  onDownload: (selectedFiles: string[]) => Promise<void>;
};

export const ArtifactDownloadDialog: React.FC<ArtifactDownloadDialogProperties> = ({ title, files, isLoading, errorMessage, submitErrorMessage, onClose, onDownload }) => {
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filter, setFilter] = useState("");
  const [dialogWidth, setDialogWidth] = useState(720);
  const [isResizingWidth, setIsResizingWidth] = useState(false);
  const dialogContentReference = useRef<HTMLDivElement | null>(null);
  const resizeStartReference = useRef({ mouseX: 0, width: 720 });

  useEffect(() => {
    setSelectedFiles(files.map((file) => file.path));
  }, [files]);

  useEffect(() => {
    if (!isResizingWidth) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      const maxWidth = Math.max(window.innerWidth - 48, 520);
      setDialogWidth(Math.min(maxWidth, Math.max(520, resizeStartReference.current.width + (event.clientX - resizeStartReference.current.mouseX))));
    };

    const handleMouseUp = () => {
      setIsResizingWidth(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizingWidth]);

  const visibleFiles = useMemo(() => {
    const normalizedFilter = filter.trim().toLowerCase();
    return normalizedFilter ? files.filter((file) => file.path.toLowerCase().includes(normalizedFilter)) : files;
  }, [files, filter]);

  const allVisibleSelected = visibleFiles.length > 0 && visibleFiles.every((file) => selectedFiles.includes(file.path));

  const toggleFile = (path: string) => {
    setSelectedFiles((current) => (current.includes(path) ? current.filter((value) => value !== path) : [...current, path]));
  };

  const toggleVisibleFiles = () => {
    if (allVisibleSelected) {
      const visiblePaths = new Set(visibleFiles.map((file) => file.path));
      setSelectedFiles((current) => current.filter((path) => !visiblePaths.has(path)));
      return;
    }

    setSelectedFiles((current) => [...new Set([...current, ...visibleFiles.map((file) => file.path)])]);
  };

  const handleDownload = async () => {
    setIsSubmitting(true);
    try {
      await onDownload(selectedFiles);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResizeStart = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const dialogWidthFromDom = dialogContentReference.current?.closest(".k-dialog")?.getBoundingClientRect().width;
    resizeStartReference.current = {
      mouseX: event.clientX,
      width: dialogWidthFromDom ?? dialogWidth,
    };
    setIsResizingWidth(true);
  };

  const renderFilesList = () => {
    if (isLoading) return <div style={{ padding: 16 }}>Loading files...</div>;
    if (errorMessage) return <div style={{ padding: 16, color: "#b42318" }}>{errorMessage}</div>;
    if (visibleFiles.length === 0) return <div style={{ padding: 16 }}>No files found.</div>;

    return visibleFiles.map((file) => {
      const checked = selectedFiles.includes(file.path);
      return (
        <label
          key={file.path}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 12px",
            borderBottom: "1px solid #f1f1f1",
            cursor: "pointer",
            backgroundColor: checked ? "#f5f9ff" : "transparent",
          }}
        >
          <Checkbox
            checked={checked}
            onChange={(event) => {
              event.syntheticEvent?.stopPropagation();
              toggleFile(file.path);
            }}
          />
          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <span style={{ fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.path}</span>
            {file.sizeInBytes > 0 && <span style={{ fontSize: 12, color: "#6b6b6b" }}>{formatBytes(file.sizeInBytes)}</span>}
          </div>
        </label>
      );
    });
  };

  return (
    <Dialog title={title} onClose={onClose} width={dialogWidth}>
      <div ref={dialogContentReference} style={{ position: "relative" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, minHeight: 360, paddingRight: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <input
              type="text"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder="Filter files"
              style={{ flex: "1 1 320px", padding: "8px 10px", border: "1px solid #c7c7c7", borderRadius: 4 }}
            />
            <Button fillMode="outline" themeColor="base" onClick={toggleVisibleFiles} disabled={visibleFiles.length === 0 || isLoading || isSubmitting}>
              {allVisibleSelected ? "Clear visible" : "Select visible"}
            </Button>
          </div>

          <div style={{ fontSize: 13, color: "#5f5f5f" }}>
            {selectedFiles.length} of {files.length} files selected
          </div>

          <div style={{ border: "1px solid #e0e0e0", borderRadius: 6, overflow: "hidden", minHeight: 240, maxHeight: 360, overflowY: "auto" }}>{renderFilesList()}</div>

          {submitErrorMessage && (
            <div role="alert" style={{ fontSize: 13, color: "#b42318" }}>
              {submitErrorMessage}
            </div>
          )}
        </div>
        <DialogActionsBar>
          <Button onClick={onClose} fillMode="outline">
            Cancel
          </Button>
          <Button themeColor="primary" onClick={handleDownload} disabled={selectedFiles.length === 0 || isLoading || isSubmitting || !!errorMessage}>
            {isSubmitting ? "Downloading..." : `Download ${selectedFiles.length} file${selectedFiles.length === 1 ? "" : "s"}`}
          </Button>
        </DialogActionsBar>
        <div
          onMouseDown={handleResizeStart}
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: 8,
            cursor: "ew-resize",
          }}
        />
      </div>
    </Dialog>
  );
};

const formatBytes = (size: number) => {
  if (size < 1024) {
    return `${size} B`;
  }

  const units = ["KB", "MB", "GB", "TB"];
  let value = size / 1024;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unitIndex]}`;
};
